import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Seo } from "../components/cod/Seo";
import { bdApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Building2, Users, Phone, Mail, FileText, TrendingUp, ClipboardList, Target, Plus, Sparkles, ArrowLeft, LayoutGrid, BarChart3, Route, Calendar, HeartHandshake, Landmark } from "lucide-react";
import { BDKanban, BDPipelines, BDAnalytics, CATEGORY_LABELS } from "./BDHub";
import { BDCalendar, BDTasks } from "./BDCalendar";
import { ModuleUnderConfiguration } from "../components/cod/ModulePlaceholder";
import client, { API } from "../lib/api";

const STAGE_LABELS = {
  prospect: "Prospect", initial_contact: "Initial Contact", discovery: "Discovery",
  relationship_building: "Relationship Building", meeting_scheduled: "Meeting Scheduled",
  proposal_mou: "Proposal / MOU", active_partner: "Active Partner",
  inactive: "Inactive", closed: "Closed",
};
const CLASSIFICATION_LABELS = {
  employer: "Employer", community_partner: "Community Partner",
  training_partner: "Training Partner", government_partner: "Government Partner",
  funding_partner: "Funding Partner", strategic_partner: "Strategic Partner",
  vendor: "Vendor", customer: "Customer",
};
const ACTIVITY_ICONS = { call: Phone, meeting: Users, email: Mail, followup: ClipboardList, note: FileText };

async function downloadCsv(type) {
  try {
    const res = await client.get(`/bd/export`, { params: { type }, responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    const disp = res.headers?.["content-disposition"] || "";
    const m = /filename="([^"]+)"/.exec(disp);
    a.download = m ? m[1] : `cod_${type}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
}

function StatCard({ icon: Icon, label, value, testid }) {  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="text-2xl font-serif text-slate-900 mt-1">{value ?? "—"}</div>
        </div>
        {Icon && <div className="rounded-md bg-slate-100 p-2"><Icon size={18} className="text-[var(--cod-navy)]"/></div>}
      </CardContent>
    </Card>
  );
}


// ═════════════════════ Institutional Development Hub (tabbed) ═════════════════════
const BD_TABS = [
  { id: "dashboard",     label: "Dashboard",           icon: TrendingUp },
  { id: "organizations", label: "Organizations",       icon: Building2 },
  { id: "opportunities", label: "Opportunities",       icon: Target },
  { id: "pipelines",     label: "Pipelines",           icon: Route },
  { id: "grants",        label: "Grants",              icon: Landmark },
  { id: "contracts",     label: "Gov Contracts",       icon: Landmark },
  { id: "foundations",   label: "Foundations",         icon: Landmark },
  { id: "sponsors",      label: "Corporate Sponsors",  icon: Building2 },
  { id: "donors",        label: "Donors",              icon: HeartHandshake },
  { id: "volunteers",    label: "Volunteers",          icon: Users },
  { id: "calendar",      label: "Calendar",            icon: Calendar },
  { id: "tasks",         label: "Tasks",               icon: ClipboardList },
  { id: "analytics",     label: "Analytics",           icon: BarChart3 },
];

function BDTabsBar({ active, onChange }) {
  return (
    <div className="border-b border-slate-200 mb-6 overflow-x-auto" data-testid="bd-tabs">
      <div className="flex gap-1 min-w-max">
        {BD_TABS.map(t => {
          const Icon = t.icon;
          const on = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              data-testid={`bd-tab-${t.id}`}
              className={`px-3 py-2.5 flex items-center gap-1.5 text-sm border-b-2 transition ${on ? "border-[var(--cod-red)] text-slate-900 font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            >
              <Icon size={14}/>
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BDDashboardPanel({ data }) {
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="bd-dashboard-stats">
        <StatCard icon={Target} label="Prospects" value={data?.prospects} testid="stat-prospects"/>
        <StatCard icon={TrendingUp} label="Active Opportunities" value={data?.active_opportunities} testid="stat-active-opps"/>
        <StatCard icon={ClipboardList} label="Follow-ups Due" value={data?.followups_due} testid="stat-followups"/>
        <StatCard icon={Users} label="Meetings Scheduled" value={data?.meetings_scheduled} testid="stat-meetings"/>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="bd-partner-stats">
        <StatCard icon={Building2} label="Employer Partners" value={data?.employer_partners} testid="stat-employer-partners"/>
        <StatCard icon={Building2} label="Community Partners" value={data?.community_partners} testid="stat-community-partners"/>
        <StatCard icon={Building2} label="Training Partners" value={data?.training_partners} testid="stat-training-partners"/>
        <StatCard icon={Building2} label="Government Partners" value={data?.government_partners} testid="stat-government-partners"/>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Recent Activity</CardTitle></CardHeader>
          <CardContent className="space-y-3" data-testid="bd-recent-activity">
            {(data?.recent_activity || []).length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
            {(data?.recent_activity || []).slice(0, 12).map(a => {
              const Icon = ACTIVITY_ICONS[a.type] || FileText;
              return (
                <div key={a.id} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-b-0" data-testid={`activity-${a.id}`}>
                  <div className="rounded-md bg-slate-100 p-2 flex-shrink-0"><Icon size={14}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{a.subject}</div>
                    <div className="text-xs text-slate-600">{a.organization?.name || "—"} · {a.type} · {a.created_at?.slice(0, 10)}</div>
                    {a.body && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{a.body}</div>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Public Leads</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-serif text-slate-900">{data?.new_leads ?? 0}</div>
            <div className="text-xs text-slate-600 mt-1">new since last check</div>
            <Link to="/staff/leads"><Button variant="outline" className="mt-4 w-full" data-testid="btn-goto-leads">Review Leads →</Button></Link>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function BDCategoryPanel({ category, title, purpose }) {
  const [opps, setOpps] = useState([]);
  useEffect(() => { bdApi.listOpps({ category }).then(setOpps).catch(()=>setOpps([])); }, [category]);
  return (
    <div data-testid={`bd-cat-${category}`}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-600 mt-1">{purpose}</p>
      </div>
      {opps.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
          No {title.toLowerCase()} yet. <button className="cod-link" onClick={()=>window.dispatchEvent(new CustomEvent("bd-tab", { detail: "opportunities" }))}>Create one →</button>
        </div>
      )}
      {opps.length > 0 && (
        <div className="space-y-2">
          {opps.map(o => (
            <div key={o.id} className="rounded border border-slate-200 bg-white p-3 flex items-center justify-between">
              <div className="min-w-0">
                <Link to={`/staff/bd/organizations/${o.organization_id}`} className="font-semibold text-slate-900 hover:text-[var(--cod-red)]">{o.title}</Link>
                <div className="text-xs text-slate-500 mt-0.5">stage: {o.stage} · {o.funder_name || o.rfp_number || ""}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-emerald-700">{o.estimated_value ? `$${o.estimated_value.toLocaleString()}` : "—"}</div>
                <div className="text-xs text-slate-500">{o.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BDDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("dashboard");
  useEffect(() => { bdApi.dashboard().then(setData).catch(()=>setData({})); }, []);
  useEffect(() => {
    const handler = (e) => setTab(e.detail || "opportunities");
    window.addEventListener("bd-tab", handler);
    return () => window.removeEventListener("bd-tab", handler);
  }, []);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  return (
    <AppShell>
      <Seo title="Institutional Development Hub" path="/staff/bd" noindex/>
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Business Development</div>
          <h1 className="font-serif text-3xl text-slate-900 mt-1">Institutional Development Hub</h1>
          <div className="cod-rule-red mt-3"/>
          <p className="text-sm text-slate-600 mt-3 max-w-3xl">The central operating dashboard for organizational growth — partnerships, grants, contracts, employer relationships, foundations, sponsorships, donors, and volunteers. Single source of truth for institutional development.</p>
        </div>
        <div className="flex flex-col gap-1 text-right" data-testid="bd-export-menu">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Export</div>
          <div className="flex gap-1 flex-wrap justify-end">
            <Button size="sm" variant="outline" onClick={()=>downloadCsv("organizations")} data-testid="btn-export-hub-orgs"><FileText size={12} className="mr-1"/>Orgs</Button>
            <Button size="sm" variant="outline" onClick={()=>downloadCsv("opportunities")} data-testid="btn-export-hub-opps"><FileText size={12} className="mr-1"/>Opps</Button>
            <Button size="sm" variant="outline" onClick={()=>downloadCsv("activities")} data-testid="btn-export-hub-activities"><FileText size={12} className="mr-1"/>Activities</Button>
            <Button size="sm" variant="outline" onClick={()=>downloadCsv("contacts")} data-testid="btn-export-hub-contacts"><FileText size={12} className="mr-1"/>Contacts</Button>
          </div>
        </div>
      </div>

      <BDTabsBar active={tab} onChange={setTab}/>

      {tab === "dashboard" && <BDDashboardPanel data={data}/>}

      {tab === "organizations" && (
        <div>
          <div className="mb-3 flex justify-end"><Link to="/staff/bd/organizations"><Button className="cod-bg-red" data-testid="btn-full-org-list">Open Full Organizations Workspace →</Button></Link></div>
          <p className="text-sm text-slate-600">All organizations across every classification live in one workspace — Employers, Community, Training, Government, Funding, Strategic partners, plus Corporate Sponsors, Faith Organizations, and Individual Donors. Multi-classification is supported so one record covers every relationship.</p>
        </div>
      )}

      {tab === "opportunities" && <BDKanban/>}
      {tab === "pipelines" && <BDPipelines isAdmin={isAdmin}/>}
      {tab === "grants" && <BDCategoryPanel category="grant" title="Grants" purpose="LOIs, proposals, awards, and reporting deadlines. Each grant lives on the Grant Pipeline stages: Opportunity Identified → LOI → Full Proposal → Submitted → Under Review → Awarded → Active Grant → Reporting → Closed."/>}
      {tab === "contracts" && <BDCategoryPanel category="government_contract" title="Government Contracts" purpose="RFPs, bid decisions, proposals, evaluations, and awarded contracts."/>}
      {tab === "foundations" && <BDCategoryPanel category="foundation" title="Foundation Cultivation" purpose="Research, initial contact, LOI, proposal, site visits, and stewardship."/>}
      {tab === "sponsors" && <BDCategoryPanel category="corporate_sponsorship" title="Corporate Sponsorships" purpose="Sponsorship prospects, pitches, agreements, and stewardship."/>}
      {tab === "donors" && (
        <ModuleUnderConfiguration
          testid="module-donors"
          title="Donors"
          purpose="Individual, organizational, corporate, foundation, and faith-based donors all live on the same organization record with the appropriate classification (individual_donor, corporate_sponsor, faith_organization, funding_partner)."
          phase="Phase 2 — Coming next session"
          capabilities={[
            "Gift history + total lifetime giving",
            "Recurring gift tracking",
            "Year-end acknowledgment letters",
            "Stewardship cadence + touchpoint reminders",
            "Segmentation for appeals (major gift / mid / grassroots)",
          ]}
          fallback={[{ to: "/staff/bd/organizations", label: "Browse donor organizations", desc: "Filter by classification: individual_donor / corporate_sponsor / faith_organization" }]}
        />
      )}
      {tab === "volunteers" && (
        <ModuleUnderConfiguration
          testid="module-volunteers-bd"
          title="Volunteer Engagement"
          purpose="Volunteers use the existing unified auth (role=volunteer). This tab will surface volunteer engagement across the Institutional Development Hub — mentors matched to participants, event volunteer rosters, hours logged, and background-check status."
          phase="Phase 2"
          capabilities={[
            "Volunteer roster with skill + availability matching",
            "Event and cohort volunteer sign-up",
            "Hours logged automatically into grant reporting outcomes",
            "Background check + waiver tracking",
          ]}
          fallback={[{ to: "/portal/volunteer", label: "Volunteer Portal", desc: "Existing self-service portal for volunteers." }]}
        />
      )}
      {tab === "calendar" && <BDCalendar/>}
      {tab === "tasks" && <BDTasks/>}
      {tab === "analytics" && <BDAnalytics/>}
    </AppShell>
  );
}
// ═════════════════════ End Institutional Development Hub ═════════════════════


// ═════════════════════ Organizations list ═════════════════════
export function BDOrgList() {
  const [orgs, setOrgs] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [classification, setClassification] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const load = () => bdApi.listOrgs({
    ...(q ? { q } : {}),
    ...(stage !== "all" ? { stage } : {}),
    ...(classification !== "all" ? { classification } : {}),
  }).then(setOrgs).catch(()=>setOrgs([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, stage, classification]);

  return (
    <AppShell>
      <Seo title="BD · Organizations" path="/staff/bd/organizations" noindex/>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <Link to="/staff/bd" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-2"><ArrowLeft size={12}/> Dashboard</Link>
          <h1 className="font-serif text-3xl text-slate-900">Organizations</h1>
          <div className="cod-rule-red mt-3"/>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>downloadCsv("organizations")} data-testid="btn-export-orgs">
            <FileText size={16} className="mr-1"/> Export CSV
          </Button>
          <Button className="cod-bg-red" onClick={()=>setShowCreate(true)} data-testid="btn-new-org"><Plus size={16} className="mr-1"/> New Organization</Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Input placeholder="Search by name…" value={q} onChange={e=>setQ(e.target.value)} data-testid="org-search"/>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger data-testid="org-stage-filter"><SelectValue placeholder="All stages"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {Object.entries(STAGE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={classification} onValueChange={setClassification}>
          <SelectTrigger data-testid="org-class-filter"><SelectValue placeholder="All classifications"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classifications</SelectItem>
            {Object.entries(CLASSIFICATION_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid gap-3" data-testid="org-list">
        {orgs.length === 0 && <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No organizations match those filters.</div>}
        {orgs.map(o => (
          <Link key={o.id} to={`/staff/bd/organizations/${o.id}`} className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-[var(--cod-red)]" data-testid={`org-row-${o.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900">{o.name}</div>
                <div className="text-xs text-slate-600 mt-1">{o.industry || "—"} · {o.hiring_needs?.slice(0, 100) || "No hiring needs on file"}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {o.classifications?.map(c => (
                    <span key={c} className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{CLASSIFICATION_LABELS[c] || c}</span>
                  ))}
                </div>
              </div>
              <span className="text-xs px-2 py-1 rounded bg-[var(--cod-navy)] text-white whitespace-nowrap">{STAGE_LABELS[o.pipeline_stage]}</span>
            </div>
          </Link>
        ))}
      </div>
      <CreateOrgDialog open={showCreate} onOpenChange={setShowCreate} onCreated={load}/>
    </AppShell>
  );
}

function CreateOrgDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name:"", industry:"", classifications:["employer"], pipeline_stage:"prospect", hiring_needs:"" });
  const toggle = (c) => setForm(f => ({ ...f, classifications: f.classifications.includes(c) ? f.classifications.filter(x=>x!==c) : [...f.classifications, c] }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await bdApi.createOrg(form);
      toast.success("Organization created");
      onOpenChange(false);
      setForm({ name:"", industry:"", classifications:["employer"], pipeline_stage:"prospect", hiring_needs:"" });
      onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Organization</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Name</Label><Input required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="new-org-name"/></div>
          <div><Label>Industry</Label><Input value={form.industry} onChange={e=>setForm({...form, industry:e.target.value})} data-testid="new-org-industry"/></div>
          <div>
            <Label>Classifications (multi-select)</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(CLASSIFICATION_LABELS).map(([v, l]) => (
                <button key={v} type="button" onClick={()=>toggle(v)} data-testid={`new-org-class-${v}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${form.classifications.includes(v) ? "bg-[var(--cod-red)] text-white border-[var(--cod-red)]" : "bg-white text-slate-700 border-slate-200"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Pipeline Stage</Label>
            <Select value={form.pipeline_stage} onValueChange={v=>setForm({...form, pipeline_stage:v})}>
              <SelectTrigger data-testid="new-org-stage"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(STAGE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Hiring Needs</Label><textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={form.hiring_needs} onChange={e=>setForm({...form, hiring_needs:e.target.value})} data-testid="new-org-hiring"/></div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button type="submit" className="cod-bg-red" data-testid="new-org-submit">Create</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════ Organization detail ═════════════════════
export function BDOrgDetail() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const load = () => bdApi.getOrg(id).then(setOrg).catch(()=>setOrg(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!org) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;

  const setStage = async (stage) => {
    try { await bdApi.transitionStage(id, stage); toast.success(`Moved to ${STAGE_LABELS[stage]}`); load(); }
    catch { toast.error("Failed"); }
  };

  const money = (v) => v == null ? "—" : (Number(v) >= 1000 ? `$${(Number(v)/1000).toFixed(v>=1_000_000?1:0)}${v>=1_000_000?"M":"k"}` : `$${Number(v).toFixed(0)}`);
  const isEmployer = (org.classifications || []).includes("employer");

  return (
    <AppShell>
      <Seo title={`BD · ${org.name}`} path={`/staff/bd/organizations/${id}`} noindex/>
      <Link to="/staff/bd/organizations" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-2"><ArrowLeft size={12}/> All Organizations</Link>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-serif text-3xl text-slate-900" data-testid="org-name">{org.name}</h1>
          <div className="text-sm text-slate-600 mt-1">{org.industry || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {org.classifications?.map(c => (
              <span key={c} className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{CLASSIFICATION_LABELS[c] || c}</span>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-3">
          {isEmployer && (
            <a href="/portal/employer" target="_blank" rel="noreferrer" className="text-xs text-slate-600 hover:text-[var(--cod-red)]" data-testid="link-open-employer-portal">Open Employer Portal ↗</a>
          )}
          <div>
            <Label className="text-xs">Relationship Stage</Label>
            <Select value={org.pipeline_stage} onValueChange={setStage}>
              <SelectTrigger className="w-56" data-testid="org-stage-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {Object.entries(STAGE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Connected Records — "single source of truth" */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="org-connected-metrics">
        <ConnectedStat testid="ms-revenue-generated" label="Revenue Generated" value={money(org.revenue_generated)}/>
        <ConnectedStat testid="ms-revenue-pending"   label="Revenue Pending"   value={money(org.revenue_pending)}/>
        <ConnectedStat testid="ms-opps-open"         label="Open Opportunities" value={(org.opportunities || []).filter(o=>o.status==='open').length}/>
        <ConnectedStat testid="ms-participants-placed" label="Participants Placed" value={(org.participants_placed || []).length}/>
        <ConnectedStat testid="ms-certifications"    label="Certs Delivered"    value={(org.certifications_delivered || []).length}/>
        <ConnectedStat testid="ms-meetings"          label="Meetings Logged"    value={org.meetings_count ?? 0}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-2">
        <div className="lg:col-span-2 space-y-6">
          {/* Opportunities pipeline mini-panel */}
          {(org.opportunities || []).length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Revenue Opportunities ({(org.opportunities || []).length})</CardTitle>
                <Link to="/staff/bd" className="text-xs cod-link" onClick={() => setTimeout(()=>window.dispatchEvent(new CustomEvent("bd-tab", { detail: "opportunities" })), 100)}>Open Kanban →</Link>
              </CardHeader>
              <CardContent data-testid="org-opportunities" className="space-y-2">
                {(org.opportunities || []).map(o => (
                  <div key={o.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{o.title}</div>
                      <div className="text-xs text-slate-500">{o.category || o.opportunity_type} · stage: {o.stage || "—"} · {o.status}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-sm font-semibold text-emerald-700">{money(o.actual_award_value || o.estimated_value)}</div>
                      {o.deadline_at && <div className="text-[10px] text-amber-700">📅 {String(o.deadline_at).slice(0,10)}</div>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Activity Timeline</CardTitle>
              <Button size="sm" className="cod-bg-red" onClick={()=>setShowActivity(true)} data-testid="btn-log-activity"><Plus size={14} className="mr-1"/> Log activity</Button>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="org-activities">
              {(org.recent_activities || []).length === 0 && <div className="text-sm text-slate-500">No activity yet.</div>}
              {(org.recent_activities || []).map(a => {
                const Icon = ACTIVITY_ICONS[a.type] || FileText;
                return (
                  <div key={a.id} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-b-0">
                    <div className="rounded-md bg-slate-100 p-2 flex-shrink-0"><Icon size={14}/></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{a.subject}</div>
                      <div className="text-xs text-slate-500">{a.type} · {a.created_at?.slice(0, 10)}</div>
                      {a.body && <div className="text-sm text-slate-700 mt-1">{a.body}</div>}
                      {a.ai_summary && (
                        <div className="mt-2 rounded bg-blue-50 border border-blue-100 p-3">
                          <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700 mb-1 flex items-center gap-1"><Sparkles size={10}/> AI Summary</div>
                          <div className="text-xs text-blue-900 whitespace-pre-wrap">{a.ai_summary}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Participants Placed */}
          {(org.participants_placed || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Participants Placed ({org.participants_placed.length})</CardTitle></CardHeader>
              <CardContent data-testid="org-participants-placed" className="space-y-2">
                {org.participants_placed.slice(0, 10).map(r => (
                  <div key={r.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-b-0">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{r.participant?.full_name || "—"}</div>
                      <div className="text-xs text-slate-500">outcome: {r.outcome} · {r.created_at?.slice(0,10)}</div>
                    </div>
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded uppercase tracking-widest">{r.outcome}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Contacts ({org.contacts?.length || 0})</CardTitle></CardHeader>
            <CardContent data-testid="org-contacts" className="space-y-2">
              {(org.contacts || []).length === 0 && <div className="text-sm text-slate-500">No contacts yet.</div>}
              {(org.contacts || []).map(c => (
                <div key={c.id} className="border-b border-slate-100 pb-2 last:border-b-0">
                  <div className="text-sm font-semibold text-slate-900">{c.first_name} {c.last_name} {c.is_primary && <span className="text-[10px] uppercase tracking-widest bg-[var(--cod-red)] text-white px-1.5 py-0.5 rounded ml-1">Primary</span>}</div>
                  <div className="text-xs text-slate-600">{c.title || ""}</div>
                  {c.email && <div className="text-xs text-slate-600">{c.email}</div>}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Portal Users — accounts that access this org via unified auth */}
          {(org.portal_users || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Portal Accounts ({org.portal_users.length})</CardTitle></CardHeader>
              <CardContent data-testid="org-portal-users" className="space-y-2">
                {org.portal_users.map(u => (
                  <div key={u.id} className="border-b border-slate-100 pb-2 last:border-b-0">
                    <div className="text-sm font-semibold text-slate-900">{u.full_name}</div>
                    <div className="text-xs text-slate-500">{u.role} · {u.email}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Staff Touchpoints */}
          {(org.staff_touchpoints || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Staff Touchpoints ({org.staff_touchpoints.length})</CardTitle></CardHeader>
              <CardContent data-testid="org-staff-touchpoints" className="space-y-1">
                {org.staff_touchpoints.map(s => (
                  <div key={s.id} className="text-sm">
                    <span className="font-semibold text-slate-900">{s.full_name}</span>
                    <span className="text-xs text-slate-500 ml-2 capitalize">{s.role}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* MOUs */}
          {(org.mous || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">MOUs ({org.mous.length})</CardTitle></CardHeader>
              <CardContent data-testid="org-mous" className="space-y-2">
                {org.mous.map(m => (
                  <div key={m.id} className="border-b border-slate-100 pb-2 last:border-b-0">
                    <div className="text-sm font-semibold text-slate-900">{m.title}</div>
                    <div className="text-xs text-slate-500">{m.status} · {m.version} {m.expiration_date && `· expires ${String(m.expiration_date).slice(0,10)}`}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Certifications Delivered */}
          {(org.certifications_delivered || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Certifications Delivered ({org.certifications_delivered.length})</CardTitle></CardHeader>
              <CardContent data-testid="org-certifications" className="space-y-1">
                {org.certifications_delivered.slice(0, 10).map(c => (
                  <div key={c.id} className="text-xs text-slate-700 truncate">{c.name || c.title || c.category}</div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">Hiring Needs</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="org-hiring">
              {org.hiring_needs || <span className="text-slate-500">Not on file.</span>}
            </CardContent>
          </Card>
        </div>
      </div>

      <LogActivityDialog open={showActivity} onOpenChange={setShowActivity} orgId={id} onCreated={load}/>
    </AppShell>
  );
}

function ConnectedStat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-serif text-slate-900 mt-1">{value ?? "—"}</div>
    </div>
  );
}

function LogActivityDialog({ open, onOpenChange, orgId, onCreated }) {
  const [form, setForm] = useState({ type:"call", subject:"", body:"", direction:"out" });
  const [aiOut, setAiOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setForm({ type:"call", subject:"", body:"", direction:"out" }); setAiOut(null); };

  const askAI = async () => {
    if (!form.body) return toast.error("Add some notes first");
    setBusy(true);
    try { const res = await bdApi.aiSummarize({ organization_id: orgId, context: form.body, kind: form.type }); setAiOut(res); }
    catch (e) { toast.error(e.response?.data?.detail || "AI unavailable"); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const activity = await bdApi.createActivity({ organization_id: orgId, ...form });
      // If AI output present, persist it onto the activity
      if (aiOut) {
        await bdApi.patchActivity(activity.id, {
          ai_summary: aiOut.summary,
          ai_next_actions: aiOut.next_actions || [],
        });
      }
      toast.success("Activity logged");
      onOpenChange(false); reset(); onCreated && onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>{ if(!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v=>setForm({...form, type:v})}>
                <SelectTrigger data-testid="act-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="note">Note</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Direction</Label>
              <Select value={form.direction} onValueChange={v=>setForm({...form, direction:v})}>
                <SelectTrigger data-testid="act-direction"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="out">Outbound</SelectItem>
                  <SelectItem value="in">Inbound</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Subject</Label><Input required value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})} data-testid="act-subject"/></div>
          <div><Label>Notes</Label><textarea rows={6} className="w-full rounded-md border border-slate-300 p-3 text-sm" value={form.body} onChange={e=>setForm({...form, body:e.target.value})} data-testid="act-body"/></div>

          <div className="flex justify-between items-center border-t border-slate-100 pt-3">
            <Button type="button" variant="outline" onClick={askAI} disabled={busy} data-testid="btn-ai-summarize">
              <Sparkles size={14} className="mr-1"/> {busy ? "Thinking…" : "Summarize with AI"}
            </Button>
            <div className="text-xs text-slate-500">Editable draft — nothing is saved until you click Log activity.</div>
          </div>

          {aiOut && (
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 space-y-2" data-testid="ai-output">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700 flex items-center gap-1"><Sparkles size={10}/> AI Draft (edit or discard)</div>
              <div>
                <Label className="text-xs">Summary</Label>
                <textarea rows={3} value={aiOut.summary} onChange={e=>setAiOut({...aiOut, summary: e.target.value})} className="w-full rounded-md border border-slate-300 p-2 text-sm mt-1" data-testid="ai-summary-edit"/>
              </div>
              {aiOut.next_actions?.length > 0 && (
                <div>
                  <Label className="text-xs">Next Actions</Label>
                  <ul className="text-xs text-slate-700 mt-1 space-y-0.5">
                    {aiOut.next_actions.map((n, i) => <li key={i} className="flex gap-2"><span className="text-blue-500">→</span>{n}</li>)}
                  </ul>
                </div>
              )}
              {aiOut.recommended_services?.length > 0 && (
                <div>
                  <Label className="text-xs">Recommended COD Services</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {aiOut.recommended_services.map((s, i) => <span key={i} className="text-[11px] bg-white border border-blue-200 text-blue-800 px-2 py-0.5 rounded">{s}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
            <Button type="button" variant="outline" onClick={()=>{ reset(); onOpenChange(false); }}>Cancel</Button>
            <Button type="submit" className="cod-bg-red" disabled={busy} data-testid="act-submit">{busy ? "Saving..." : "Log Activity"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════ Staff Leads Inbox ═════════════════════
export function StaffLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("new");
  const load = () => bdApi.listLeads(filter !== "all" ? { status: filter } : {}).then(setLeads).catch(()=>setLeads([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const [showConvert, setShowConvert] = useState(null);

  return (
    <AppShell>
      <Seo title="Staff · Public Leads" path="/staff/leads" noindex/>
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-slate-900">Public Leads</h1>
        <div className="cod-rule-red mt-3"/>
        <p className="text-sm text-slate-600 mt-3">Inbound leads from the public Navigator. Individual inquiries route to participant intake — organization inquiries can be converted to a Prospect Organization in one click.</p>
      </div>

      <div className="flex gap-2 mb-4" data-testid="lead-filter-chips">
        {["new", "converted", "all"].map(f => (
          <button key={f} onClick={()=>setFilter(f)} data-testid={`lead-filter-${f}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === f ? "bg-[var(--cod-navy)] text-white border-[var(--cod-navy)]" : "bg-white text-slate-700 border-slate-200"}`}>
            {f === "all" ? "All" : f === "new" ? "New" : "Converted"}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="leads-list">
        {leads.length === 0 && <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No leads.</div>}
        {leads.map(l => (
          <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`lead-row-${l.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-slate-900">{l.name}</div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${l.inquirer_type === "organization" ? "bg-[var(--cod-red)] text-white" : "bg-slate-100 text-slate-700"}`}>
                    {l.inquirer_type === "organization" ? "Organization" : "Individual"}
                  </span>
                  {l.organization_name && <span className="text-xs text-slate-600">· {l.organization_name}</span>}
                </div>
                <div className="text-xs text-slate-600 mt-1">{l.email} · {l.phone || "no phone"} · {l.action} · Prefers {l.preferred_contact}</div>
                <div className="text-sm text-slate-700 mt-2 line-clamp-2">{l.needs}</div>
              </div>
              {l.inquirer_type === "organization" && l.status !== "converted" && (
                <Button size="sm" onClick={()=>setShowConvert(l)} className="cod-bg-red" data-testid={`btn-convert-${l.id}`}>Convert to Organization</Button>
              )}
              {l.status === "converted" && <span className="text-xs text-emerald-700 font-semibold">✓ Converted</span>}
            </div>
          </div>
        ))}
      </div>

      {showConvert && <ConvertLeadDialog lead={showConvert} onClose={()=>setShowConvert(null)} onDone={()=>{ setShowConvert(null); load(); }}/>}
    </AppShell>
  );
}

function ConvertLeadDialog({ lead, onClose, onDone }) {
  const [orgName, setOrgName] = useState(lead.organization_name || `${lead.name} (from lead)`);
  const [classifications, setClassifications] = useState(["employer"]);
  const navigate = useNavigate();
  const toggle = (c) => setClassifications(cs => cs.includes(c) ? cs.filter(x=>x!==c) : [...cs, c]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const res = await bdApi.convertLead(lead.id, { organization_name: orgName, classifications });
      toast.success("Prospect organization created");
      onDone();
      navigate(`/staff/bd/organizations/${res.organization.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={(v)=>{ if(!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Convert to Prospect Organization</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Organization Name</Label><Input required value={orgName} onChange={e=>setOrgName(e.target.value)} data-testid="convert-org-name"/></div>
          <div>
            <Label>Classifications</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(CLASSIFICATION_LABELS).map(([v, l]) => (
                <button key={v} type="button" onClick={()=>toggle(v)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${classifications.includes(v) ? "bg-[var(--cod-red)] text-white border-[var(--cod-red)]" : "bg-white text-slate-700 border-slate-200"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" className="cod-bg-red" data-testid="convert-submit">Convert</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
